import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronUp, FileText, Tag } from 'lucide-react'
import type { AuditReport } from '@/lib/api/amazon-listing'

interface ManualSourceInputPanelProps {
  sourceInput: NonNullable<AuditReport['source_input']>
}

function FieldRow({ label, value, fallback }: { label: string; value?: string; fallback: string }) {
  return (
    <div className="manual-source-field flex items-baseline gap-2 text-sm">
      <span className="text-neutral-500 whitespace-nowrap min-w-[80px]">{label}</span>
      <span className={value ? 'text-neutral-200' : 'text-neutral-600 italic'}>{value || fallback}</span>
    </div>
  )
}

export function ManualSourceInputPanel({ sourceInput }: ManualSourceInputPanelProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  return (
    <div className="manual-source-input-panel mt-3 border-t border-white/10 pt-3">
      <button
        onClick={() => setOpen(!open)}
        className="manual-source-toggle-btn flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors w-full"
      >
        <FileText className="h-3.5 w-3.5" />
        <span>{t('amazonListing.sourceInput.title')}</span>
        {open ? <ChevronUp className="h-3.5 w-3.5 ml-auto" /> : <ChevronDown className="h-3.5 w-3.5 ml-auto" />}
      </button>

      {open && (
        <div className="manual-source-input-details mt-3 space-y-2 pl-5">
          <FieldRow label={t('amazonListing.sourceInput.brand')} value={sourceInput.brand} fallback={t('amazonListing.sourceInput.notSpecified')} />

          <div className="text-sm">
            <span className="text-neutral-500 block mb-1">{t('amazonListing.sourceInput.bullets')}</span>
            {sourceInput.bullets.length > 0 ? (
              <ul className="list-disc list-inside space-y-0.5 text-neutral-200">
                {sourceInput.bullets.map((b, i) => (
                  <li key={i} className="line-clamp-1">{b}</li>
                ))}
              </ul>
            ) : (
              <span className="text-neutral-600 italic">{t('amazonListing.sourceInput.noBullets')}</span>
            )}
          </div>

          <div className="text-sm">
            <span className="text-neutral-500 block mb-1">{t('amazonListing.sourceInput.description')}</span>
            <p className={sourceInput.description ? 'text-neutral-200 line-clamp-3' : 'text-neutral-600 italic'}>
              {sourceInput.description || t('amazonListing.sourceInput.noDescription')}
            </p>
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <FieldRow label={t('amazonListing.sourceInput.price')} value={sourceInput.price} fallback={t('amazonListing.sourceInput.notSpecified')} />
            <FieldRow label={t('amazonListing.sourceInput.category')} value={sourceInput.category} fallback={t('amazonListing.sourceInput.notSpecified')} />
          </div>

          {(sourceInput.gtin || sourceInput.sku) && (
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              {sourceInput.gtin && (
                <div className="manual-source-gtin flex items-center gap-1.5 text-sm">
                  <Tag className="h-3 w-3 text-neutral-500" />
                  <span className="text-neutral-500">{t('amazonListing.sourceInput.gtin')}</span>
                  <span className="text-neutral-200 font-mono text-xs">{sourceInput.gtin}</span>
                </div>
              )}
              {sourceInput.sku && (
                <div className="manual-source-sku flex items-center gap-1.5 text-sm">
                  <Tag className="h-3 w-3 text-neutral-500" />
                  <span className="text-neutral-500">{t('amazonListing.sourceInput.sku')}</span>
                  <span className="text-neutral-200 font-mono text-xs">{sourceInput.sku}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
